// Feature #326 — ask a colleague to assess this person, from their profile.
//
// The dialog carries the three decisions the request has to capture, and
// each one is a sibling work item rather than a nicety:
//
// * WHO assesses (#326) — any active user other than the subject. Not
//   restricted to sm_manager: the entire point of #343 is that a subject
//   matter expert who holds no SM role can be asked.
// * WHICH skills (#346) — at least one, multi-select and searchable, drawn
//   from the whole catalogue rather than only what the subject has claimed.
//   R6 lets a peer introduce a skill the subject never listed, and a
//   manager may well want exactly that opinion.
// * WHETHER they see existing ratings (#345) — checked by default, so
//   nothing changes for a manager calibrating against a known baseline.
//   Unchecked serves a blind form, and it is the API that withholds the
//   numbers, not this screen.
import React, { useEffect, useMemo, useState } from 'react'
import {
  createAssessmentRequest,
  getSkills,
  getStaffRoster,
  SmAssessmentRequest,
  SmSkill,
} from '@/api/sm'

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  width: '100%',
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-dark-grey)',
  display: 'block',
  marginBottom: '6px',
}

const primaryButton: React.CSSProperties = {
  padding: '9px 20px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryButton: React.CSSProperties = {
  padding: '9px 20px',
  backgroundColor: 'transparent',
  color: 'var(--color-dark-grey)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

interface Props {
  subjectUserId: string
  subjectDisplayName: string
  onClose: () => void
  onCreated: (request: SmAssessmentRequest) => void
}

interface PersonOption {
  user_id: string
  display_name: string
}

export const RequestAssessmentDialog: React.FC<Props> = ({
  subjectUserId,
  subjectDisplayName,
  onClose,
  onCreated,
}) => {
  const [people, setPeople] = useState<PersonOption[]>([])
  const [skills, setSkills] = useState<SmSkill[]>([])
  const [assessorId, setAssessorId] = useState('')
  const [selectedSkills, setSelectedSkills] = useState<string[]>([])
  const [skillQuery, setSkillQuery] = useState('')
  // #345 — checked by default. Existing behaviour is the default; this is
  // opt-out, not opt-in.
  const [showRatings, setShowRatings] = useState(true)
  const [dueAt, setDueAt] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    // The SM staff roster, not `getUsers`: it is every active user,
    // unpaginated and already sm_manager-gated, so it needs no page-size
    // guess and cannot silently return the first 20 (the #337 trap).
    getStaffRoster()
      .then((staff) =>
        setPeople(
          staff
            .filter((s) => s.user_id !== subjectUserId)
            .map((s) => ({ user_id: s.user_id, display_name: s.display_name }))
        )
      )
      .catch(() => setError('Could not load the staff list'))
    getSkills()
      .then(setSkills)
      .catch(() => setError('Could not load the skills catalogue'))
  }, [subjectUserId])

  // Uncapped deliberately: the list scrolls, and truncating a catalogue
  // somebody opened in order to browse defeats the point (the same call
  // #324 made for the assessment form's picker).
  const matches = useMemo(() => {
    const q = skillQuery.trim().toLowerCase()
    const active = skills.filter((s) => s.is_active)
    if (!q) return active
    return active.filter((s) => s.name.toLowerCase().includes(q))
  }, [skills, skillQuery])

  const toggleSkill = (skillId: string) =>
    setSelectedSkills((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId]
    )

  const handleSubmit = async () => {
    setError(null)
    if (!assessorId) {
      setError('Choose who should perform the assessment.')
      return
    }
    if (selectedSkills.length === 0) {
      setError('Choose at least one skill for them to assess.')
      return
    }
    setSubmitting(true)
    try {
      const request = await createAssessmentRequest({
        subject_user_id: subjectUserId,
        assessor_user_id: assessorId,
        skill_ids: selectedSkills,
        show_current_ratings: showRatings,
        // A date input gives a local calendar day; send it as the end of
        // that day so "due 3 Sep" does not expire at midnight on the 2nd.
        due_at: dueAt ? new Date(`${dueAt}T23:59:59`).toISOString() : null,
        note: note.trim() || null,
      })
      onCreated(request)
    } catch (e: any) {
      // 409 — an open request between this pair already exists. The API
      // hands back the existing one so this can name it rather than
      // leaving the manager at a dead end.
      if (e?.response?.status === 409) {
        const existing = e?.response?.data?.detail?.request
        setError(
          existing
            ? `You have already asked ${existing.assessor_display_name ?? 'this person'} ` +
              `to assess ${subjectDisplayName}. Withdraw that request below first ` +
              'if you want to change what it covers.'
            : 'An open request already exists between these two people.'
        )
      } else {
        setError(
          e?.response?.data?.detail || e?.message || 'Failed to create the request'
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Request an assessment of ${subjectDisplayName}`}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 16px',
        zIndex: 50,
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-white)',
          borderRadius: '12px',
          padding: '24px',
          width: '100%',
          maxWidth: '640px',
          display: 'grid',
          gap: '18px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
            Request an assessment of {subjectDisplayName}
          </h2>
          <p
            style={{
              fontSize: '13px',
              color: 'var(--color-dark-grey)',
              margin: '6px 0 0',
              lineHeight: 1.6,
            }}
          >
            They will be emailed and notified in the platform, and it will appear
            under their My Assessments. Whatever they submit is advisory — it
            raises deltas for you to settle at a review, and never changes{' '}
            {subjectDisplayName}&rsquo;s ratings on its own.
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: '12px 14px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        <div>
          <label style={labelStyle} htmlFor="assessor-select">
            Who should assess them?
          </label>
          <select
            id="assessor-select"
            style={inputStyle}
            value={assessorId}
            onChange={(e) => setAssessorId(e.target.value)}
          >
            <option value="">Choose a person…</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="skill-search">
            Which skills? ({selectedSkills.length} selected)
          </label>
          <input
            id="skill-search"
            type="text"
            value={skillQuery}
            onChange={(e) => setSkillQuery(e.target.value)}
            placeholder="Search the catalogue…"
            style={inputStyle}
          />
          <div
            style={{
              marginTop: '8px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              maxHeight: '220px',
              overflowY: 'auto',
            }}
          >
            {matches.length === 0 && (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: '13px',
                  color: 'var(--color-dark-grey)',
                }}
              >
                No matching skills
              </div>
            )}
            {matches.map((skill) => (
              <label
                key={skill.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '7px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--color-light-grey)',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedSkills.includes(skill.id)}
                  onChange={() => toggleSkill(skill.id)}
                />
                <span style={{ flex: 1 }}>{skill.name}</span>
              </label>
            ))}
          </div>
          <p
            style={{
              fontSize: '12px',
              color: 'var(--color-dark-grey)',
              margin: '6px 0 0',
            }}
          >
            They will be asked about these skills only. The skills you leave
            out keep their existing ratings and staleness.
          </p>
        </div>

        <div>
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={showRatings}
              onChange={(e) => setShowRatings(e.target.checked)}
              style={{ marginTop: '3px' }}
            />
            <span>
              <strong>Show them {subjectDisplayName}&rsquo;s existing ratings</strong>
              <span
                style={{
                  display: 'block',
                  color: 'var(--color-dark-grey)',
                  marginTop: '2px',
                  lineHeight: 1.6,
                }}
              >
                Leave this on when you want them to calibrate against a known
                baseline. Turn it off for a genuinely independent view —
                showing someone a 4 invites them to agree with the 4.
              </span>
            </span>
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '14px' }}>
          <div>
            <label style={labelStyle} htmlFor="due-at">
              Due by (optional)
            </label>
            <input
              id="due-at"
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="request-note">
              Note (optional)
            </label>
            <input
              id="request-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. you two worked together on Northgate"
              style={inputStyle}
              maxLength={1000}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={secondaryButton}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              ...primaryButton,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RequestAssessmentDialog
